import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import Message from 'components/Message';
import { openModal } from 'components/Modal';
import { SmartSpotStatus, updateSmartSpot } from 'components/SmartSpot';
import { PlainSwitch as Switch } from 'components/Switch';
import { SMARTSPOT_REASON } from 'constants/logic';
import { schedulePropTypes } from 'constants/propTypes';
import { bem } from 'helpers/class-name';
import { selectCanOnlyToggleNonRecurringSmartSpot, selectClassSmartspotCanTurnOnOff } from 'selectors/general';

import { reservationsOverviewMessages } from '../ScheduleDetail/ScheduleDetail.messages';
import AllBookingsSection from './AllBookingsSection';
import AllBookingsStatCard from './AllBookingsStatCard';
import ClassPassBookingsCard from './ClassPassBookingsCard';
import messages from './ScheduleDetailV2.messages';
import './BookingOverview.scss';

const { block, element } = bem('booking-overview');

const SmartSpotClassToggle = ({ schedule }) => {
  const dispatch = useDispatch();
  const canToggleClassSmartSpot = useSelector(selectClassSmartspotCanTurnOnOff);
  const canOnlyToggleNonRecurring = useSelector(selectCanOnlyToggleNonRecurringSmartSpot);
  const { isDisabled, scheduleClass, scheduleId, scheduleName, smartspotEnabled, startDateTime, venueId } = schedule;
  const [isEnabled, setIsEnabled] = useState(Boolean(smartspotEnabled));

  useEffect(() => {
    setIsEnabled(Boolean(smartspotEnabled));
  }, [scheduleId, smartspotEnabled]);

  const handleChange = event => {
    const nextEnabled = event.target.checked;
    setIsEnabled(nextEnabled);

    if (nextEnabled && canOnlyToggleNonRecurring) {
      dispatch(
        updateSmartSpot(
          { isEnabled: nextEnabled },
          {
            classId: scheduleClass?.classId,
            className: scheduleName,
            isModal: false,
            reason: SMARTSPOT_REASON.SCHEDULE_SETTING,
            scheduleId,
            startDateTime,
            venueId,
          }
        )
      );
      return;
    }

    dispatch(
      openModal('ManageSmartSpotModal', {
        schedule: { ...schedule, smartspotEnabled: nextEnabled, canOnlyToggleNonRecurring },
        onClose: () => setIsEnabled(Boolean(smartspotEnabled)),
      })
    );
  };

  return (
    <span className={element('smartspot-status')} data-cypress="booking-overview-smartspot-status">
      <SmartSpotStatus isEnabled={isEnabled} showIndicator={false}>
        <Message message={isEnabled ? reservationsOverviewMessages.ss.on : reservationsOverviewMessages.ss.off} />
      </SmartSpotStatus>
      {canToggleClassSmartSpot && !isDisabled && (
        <Switch
          checked={isEnabled}
          className={element('smartspot-switch')}
          inputProps={{
            'aria-label': 'SmartSpot',
            'data-cypress': 'booking-overview-smartspot-toggle',
            onChange: handleChange,
          }}
          name={`booking-overview-smartspot-${scheduleId}`}
        />
      )}
    </span>
  );
};

SmartSpotClassToggle.propTypes = {
  schedule: PropTypes.shape(schedulePropTypes).isRequired,
};

const BookingOverview = ({ schedule }) => {
  const { hasIntegrationData, isInThePast, smartspotEnabledForVenue } = schedule;

  return (
    <section className={block()} data-cypress="booking-overview">
      <div className={element('header')}>
        <Message as="h3" className={element('title')} message={messages.bookingOverviewTitle} />
        {smartspotEnabledForVenue && !isInThePast && <SmartSpotClassToggle schedule={schedule} />}
      </div>
      <div className={element('cards')}>
        <ClassPassBookingsCard schedule={schedule} />
        {hasIntegrationData && <AllBookingsStatCard schedule={schedule} />}
      </div>
      <AllBookingsSection schedule={schedule} />
    </section>
  );
};

BookingOverview.propTypes = {
  schedule: PropTypes.shape(schedulePropTypes).isRequired,
};

export default BookingOverview;
